import { Package } from '../types';
import { PACKAGES_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const usePackagesApi = () => {

  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getPackages = async (getAllPackages: boolean = false): Promise<Package[]> => {
    try {
      const headers = await getHeaders();

      const response = await fetch(
        `${PACKAGES_URL}?getAllPackages=${getAllPackages}`,
        {
          method: 'GET',
          headers
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch Packages');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching Packages:', error);
      throw error;
    }
  };

  const getPackage = async (id: string): Promise<Package> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PACKAGES_URL}/${id}`,
        {
          method: 'GET',
          headers
        }
      );
      console.log('[getPackage] Response:', response);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch Package';
        let errorDetails;
        
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching Package:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const createPackage = async (packageAgenticgenie: Omit<Package, 'id'>): Promise<Package> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PACKAGES_URL}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(packageAgenticgenie),
        }
      );

      if (!response.ok) {
        console.log('[createPackage] Error creating Package response:', response);
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          console.log('[createPackage] Error creating Package errorData:', errorData);
          throw new Error(errorData.message || 'Failed to create Package');
        } catch (parseError) {
          console.log('[createPackage] Error parsing JSON:', String(parseError));
          console.log('[createPackage] Error parsing JSON Type:', typeof (parseError));
          throw new Error(String(parseError));
        }
      }
      return response.json();
    } catch (error) {
      console.error('Error creating Package:', error);
      throw error;
    }
  };

  const updatePackage = async (packageAgenticgenie: Partial<Package>): Promise<Package> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PACKAGES_URL}/${packageAgenticgenie.packageId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(packageAgenticgenie),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update Package');
      }
      return response.json();
    } catch (error) {
      console.error('Error updating Package:', error);
      throw error;
    }
  };

  const deletePackage = async (id: string): Promise<void> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PACKAGES_URL}/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete Package');
      }
      return;
    } catch (error) {
      console.error('Error deleting Package:', error);
      throw error;
    }
  };

  return {
    getPackages,
    getPackage,
    createPackage,
    updatePackage,
    deletePackage
  };
}